// Dependencies
import { nanoid } from "nanoid/non-secure";
import createHook from "zustand";
import create from "zustand/vanilla";

import { IconDefinition } from "@fortawesome/pro-regular-svg-icons";

// Api

export interface Toast {
	/**
	 * An id for the toast. This is generated automatically using nanoid.
	 */
	id: string;
	/**
	 * The message to display in the toast.
	 * @example "Hello World"
	 */
	message: string;
	/**
	 * The type of toast to display.
	 */
	type: "success" | "error" | "warning" | "info";
	/**
	 * The duration to display the toast for.
	 * @default 5000
	 */
	duration?: number;
	/**
	 * The size of the toast.
	 * @default "small"
	 */
	size?: "small" | "medium" | "large";
	/**
	 * icon to display in the toast.
	 */
	icon?: IconDefinition;
}

export interface AddToastProps extends Omit<Toast, "id"> {}

interface ToastStore {
	toasts: Toast[];
	addToast: (props: AddToastProps) => void;
	removeToast: (id: string) => void;
}

// the number of toasts to hold in the store
const TOAST_COUNT = 8;

export const toastStore = create<ToastStore>((set) => ({
	toasts: [],
	addToast: (props) => {
		// create toast
		const toast: Toast = {
			id: nanoid(),
			...props,
		};

		// update the state, only hold on to the last 5 toasts
		set((state) => ({
			toasts: [...state.toasts.slice(-TOAST_COUNT + 1), toast],
		}));
	},
	removeToast: (id: string) => {
		set((state) => ({
			toasts: state.toasts.filter((toast) => toast.id !== id),
		}));
	},
}));

const useToastStore = createHook<ToastStore>(toastStore);
export default useToastStore;
